<template>
  <div class="container mx-auto px-3 py-6 sm:px-4 sm:py-8">
    <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8">
      Browse by Categories
    </h1>
    
    <!-- Category Filter -->
    <CategoryFilter
      :categories="availableCategories"
      :selected-category="selectedCategory"
      :loading="loading"
      @category-select="handleCategorySelect"
    />

    <!-- View Mode Toggle -->
    <div class="flex items-center justify-end mb-4">
      <div class="flex items-center space-x-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
        <button @click="viewMode = 'grid'" :class="[
          'px-3 py-1 text-sm rounded-md transition-colors',
          viewMode === 'grid'
            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
        ]">
          Grid
        </button>
        <button @click="viewMode = 'preview'" :class="[
          'px-3 py-1 text-sm rounded-md transition-colors',
          viewMode === 'preview'
            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
        ]">
          Device Preview
        </button>
      </div>
    </div>

    <!-- Ad Banner before wallpapers -->
    <GoogleAd ad-type="banner" class="mb-6" :min-content-height="400" />

    <!-- Wallpaper Grid -->
    <WallpaperGrid
      :wallpapers="filteredWallpapers"
      :loading="loading"
      :has-more="hasMore"
      :view-mode="viewMode"
      @wallpaper-click="viewWallpaper"
      @load-more="loadMoreWallpapers"
    />

    <!-- Ad Rectangle after wallpapers if we have content -->
    <GoogleAd 
      v-if="filteredWallpapers.length > 6" 
      ad-type="rectangle" 
      class="mt-8 flex justify-center" 
      :min-content-height="800" 
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import WallpaperGrid from '../components/WallpaperGrid.vue';
import CategoryFilter from '../components/CategoryFilter.vue';
import GoogleAd from '../components/GoogleAd.vue';
import { wallpaperService, type Wallpaper } from '../firebase/services/wallpaperService';
import { userService } from '../firebase/services/userService';
import { useAuth } from '../composables/useAuth';
import type { DocumentSnapshot } from 'firebase/firestore';

const router = useRouter();

// State
const viewMode = ref('grid'); // 'grid' or 'preview'
const categories = ref<string[]>([]);
const selectedCategory = ref('All');
const wallpapers = ref<Wallpaper[]>([]);
const loading = ref(false);
const hasMore = ref(true);
const userFavorites = ref<string[]>([]);
const lastDoc = ref<DocumentSnapshot | null>(null);

const { currentUser } = useAuth();

// Computed
const filteredWallpapers = computed(() => {
  if (selectedCategory.value === 'All') {
    return wallpapers.value;
  }
  return wallpapers.value.filter(wallpaper => 
    (wallpaper.category ?? '').toLowerCase() === selectedCategory.value.toLowerCase()
  );
});


const availableCategories = computed(() => {
  return categories.value.filter(category => category !== 'All');
});

// Methods
const loadCategories = async () => {
  try {
    const categoryList = await wallpaperService.getCategories();
    categories.value = ['All', ...categoryList];
  } catch (error) {
    console.error('Error loading categories:', error);
  }
};

const loadWallpapers = async () => {
  if (loading.value) return;
  
  loading.value = true;
  try {
    const result = await wallpaperService.getWallpapers({
      category: selectedCategory.value === 'All' ? undefined : selectedCategory.value,
      limitCount: 20
    });
    wallpapers.value = result.wallpapers;
    lastDoc.value = result.lastDoc;
    hasMore.value = result.wallpapers.length === 20;
  } catch (error) {
    console.error('Error loading wallpapers:', error);
  } finally {
    loading.value = false;
  }
};

const loadMoreWallpapers = async () => {
  if (loading.value || !hasMore.value) return;
  
  loading.value = true;
  try {
    const result = await wallpaperService.getWallpapers({
      category: selectedCategory.value === 'All' ? undefined : selectedCategory.value,
      limitCount: 20,
      lastDoc: lastDoc.value ?? undefined
    });
    wallpapers.value.push(...result.wallpapers);
    lastDoc.value = result.lastDoc;
    hasMore.value = result.wallpapers.length === 20;
  } catch (error) {
    console.error('Error loading more wallpapers:', error);
  } finally {
    loading.value = false;
  }
};

const loadUserFavorites = async () => {
  if (!currentUser.value) {
    userFavorites.value = [];
    return;
  }
  
  try {
    const favorites = await userService.getUserFavorites(currentUser.value.uid);
    userFavorites.value = favorites.map(fav => fav.wallpaperId);
  } catch (error) {
    console.error('Error loading user favorites:', error);
  }
};

const viewWallpaper = (wallpaper: Wallpaper) => {
  router.push(`/wallpaper/${wallpaper.id}`);
};

const handleCategorySelect = (category: string | null) => {
  selectedCategory.value = category || 'All';
};

// Lifecycle
onMounted(async () => {
  // Load initial data
  await Promise.all([
    loadCategories(),
    loadWallpapers()
  ]);
});

watch(currentUser, async () => {
  await loadUserFavorites();
}, { immediate: true });

// Watch for category changes
watch(selectedCategory, () => {
  // Reset pagination state when category changes
  lastDoc.value = null;
  hasMore.value = true;
  loadWallpapers();
});
</script>